import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";

export interface ZoneLogNoticesProps {
  servicesMissing: boolean;
  zonelogsEmpty: boolean;
  noTrackedZones: boolean;
  /** Rules are missing, but this user may not create them (narrower data scope). */
  setupNeedsAdmin: boolean;
  /** Zone types recorded only from a moment inside the period; before it an empty table would be misleading. */
  lateZones: { name: string; from: Date }[];
}

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function ZoneLogNotices({
  servicesMissing,
  zonelogsEmpty,
  noTrackedZones,
  setupNeedsAdmin,
  lateZones,
}: ZoneLogNoticesProps) {
  const { t } = useTranslation();
  const notices = [
    servicesMissing && { severity: "error" as const, text: t("zoneReportPage.notices.servicesMissing") },
    zonelogsEmpty && { severity: "warning" as const, text: t("zoneReportPage.notices.zonelogsEmpty") },
    noTrackedZones && { severity: "warning" as const, text: t("zoneReportPage.notices.noTrackedZones") },
    setupNeedsAdmin && { severity: "warning" as const, text: t("zoneReportPage.notices.setupNeedsAdmin") },
  ].filter(notice => !!notice);

  if (notices.length === 0 && lateZones.length === 0) return null;
  return (
    <Stack spacing={1}>
      {notices.map(notice => (
        <Alert key={notice.text} severity={notice.severity}>
          {notice.text}
        </Alert>
      ))}
      {lateZones.length > 0 && (
        <Alert severity="info">
          {lateZones.map(zone => (
            <div key={zone.name}>
              {t("zoneReportPage.notices.dataFrom", { zone: zone.name, date: DATE_FORMAT.format(zone.from) })}
            </div>
          ))}
          <div>{t("zoneReportPage.notices.reprocessHint")}</div>
        </Alert>
      )}
    </Stack>
  );
}
